// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import { type QueryClient, useSuspenseQuery } from "@tanstack/react-query";

import { versionQuery } from "@/api/mas";

type MasFeature = "personalTokens" | "devices";

export type MasFeaturesStatus = Record<MasFeature, boolean>;

const computeFeatures = (version: string): MasFeaturesStatus => ({
  personalTokens: !version.startsWith("v0.0.0"),
  devices: !version.startsWith("v0.0.0"),
});

/**
 * A hook to get the availability of all the features on the given server
 *
 * @param serverName The server name to which the query is sent
 * @returns A record indicating which features are available
 */
export const useFeaturesStatus = (serverName: string): MasFeaturesStatus => {
  const { data } = useSuspenseQuery(versionQuery(serverName));
  return computeFeatures(data.version);
};

/**
 * Get the availability of all the features on the given server
 *
 * @param queryClient The Tanstack Query client to use
 * @param serverName The server name to which the query is sent
 * @returns A record indicating which features are available
 */
export const getFeaturesStatus = async (
  queryClient: QueryClient,
  serverName: string,
): Promise<MasFeaturesStatus> => {
  const data = await queryClient.ensureQueryData(versionQuery(serverName));
  return computeFeatures(data.version);
};
